import {
  Heart,
  Instagram,
  MessageCircle,
  Phone,
  Search,
  ShoppingBag,
  User,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Wordmark } from '@/components/brand/wordmark';
import { CartDrawer } from '@/features/cart/cart-drawer';
import { SearchOverlay } from '@/features/search/search-overlay';
import { useCartItemCount } from '@/hooks/useCart';
import { useFavoriteCount } from '@/hooks/useFavorites';
import { useCategories, useLocalSeoPages } from '@/hooks/useStorefront';
import { useStoreSettings } from '@/hooks/useStoreSettings';
import { buildWhatsAppUrl, cn, toTelNumber } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';

/**
 * Footer'daki kurumsal bağlantılar.
 *
 * Hukuki metinler içerikleri yayınlanmamış olsa bile burada listelenir:
 * kullanıcı metnin nerede olacağını görür ve sayfa dürüst bir boş durum
 * gösterir. Gizlemek, metnin hiç olmadığını düşündürürdü.
 */
const CORPORATE_LINKS = [
  { to: '/hakkimizda', label: 'Hakkımızda' },
  { to: '/iletisim', label: 'İletişim' },
  { to: '/sikca-sorulan-sorular', label: 'Sıkça Sorulan Sorular' },
  { to: '/teslimat-bilgileri', label: 'Teslimat Bilgileri' },
  { to: '/mesafeli-satis', label: 'Mesafeli Satış Sözleşmesi' },
  { to: '/on-bilgilendirme', label: 'Ön Bilgilendirme Formu' },
  { to: '/iptal-iade', label: 'İptal ve İade' },
  { to: '/gizlilik', label: 'Gizlilik Politikası' },
  { to: '/kvkk', label: 'KVKK Aydınlatma Metni' },
  { to: '/kullanim-kosullari', label: 'Kullanım Koşulları' },
];


export function StorefrontLayout() {
  const { data: settings } = useStoreSettings();
  const { data: categories } = useCategories();
  const { pathname } = useLocation();
  const favoriteCount = useFavoriteCount();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const cartCount = useCartItemCount();
  const openOverlay = useUiStore((state) => state.openOverlay);

  // Rota değişince sayfa başına dön — liste → detay → geri akışında şart.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  const storeName = settings?.['store.name'] ?? 'Hayal Çiçek & Hediyelik';
  const phone = settings?.['store.phone'];
  const whatsapp = settings?.['store.whatsapp'];
  const whatsappTemplate =
    settings?.['whatsapp.generalTemplate'] ?? 'Merhaba, bilgi almak istiyorum.';

  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <a
        href="#main"
        className="sr-only-focusable absolute left-4 top-4 z-50 rounded-sm bg-surface px-4 py-2 text-sm shadow-soft"
      >
        İçeriğe geç
      </a>

      <header className="sticky top-0 z-40 border-b border-line bg-paper/85 backdrop-blur-md">
        <div className="container-page flex h-[--header-height] items-center justify-between gap-4">
          <Link to="/" aria-label={storeName} className="-ml-1 min-w-0 shrink p-1">
            <Wordmark storeName={storeName} size="sm" className="items-start" />
          </Link>

          {/* Masaüstü kategori navigasyonu — veriden gelir, kodda sabit değil */}
          {/*
           * Yedi kategori ancak xl'den itibaren sığar: 1024px'te başlık satırı
           * (logo + ikonlar) geriye ~630px bırakıyor, menü ise ~865px istiyor.
           * Daha dar ekranlarda alttaki kaydırılabilir şerit devreye girer.
           */}
          <nav aria-label="Kategoriler" className="hidden xl:block">
            <ul className="flex items-center gap-4 2xl:gap-6">
              {categories?.slice(0, 7).map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/kategori/${category.slug}`}
                    className={cn(
                      'whitespace-nowrap text-sm text-ink-muted transition-colors duration-fast',
                      'hover:text-ink',
                      pathname === `/kategori/${category.slug}` && 'text-ink',
                    )}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex shrink-0 items-center gap-0 sm:gap-1">
            <button
              type="button"
              onClick={() => openOverlay('search')}
              aria-label="Ürün ara"
              className="flex size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink"
            >
              <Search className="size-[18px]" aria-hidden />
            </button>

            <Link
              to="/favorilerim"
              aria-label={
                favoriteCount > 0
                  ? `Favorilerim, ${favoriteCount} ürün`
                  : 'Favorilerim'
              }
              className="relative flex size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink"
            >
              <Heart
                className={cn(
                  'size-[18px]',
                  favoriteCount > 0 && 'fill-blush-600 text-blush-600',
                )}
                aria-hidden
              />

              {favoriteCount > 0 ? (
                <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-pill bg-blush-600 px-1 text-[10px] font-medium tabular-nums text-white">
                  {favoriteCount}
                </span>
              ) : null}
            </Link>

            {/*
              Hesap girişi. Üyelik satın almak için zorunlu değildir; bu
              bağlantı yalnızca siparişlerini takip etmek isteyene kapı açar.
            */}
            <Link
              to={isAuthenticated ? '/hesabim' : '/giris'}
              aria-label={isAuthenticated ? 'Hesabım' : 'Giriş yap'}
              className="flex size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink"
            >
              <User className="size-[18px]" aria-hidden />
            </Link>

            <button
              type="button"
              onClick={() => openOverlay('cart')}
              aria-label={
                cartCount > 0
                  ? `Sepet, ${cartCount} ürün`
                  : 'Sepet'
              }
              className="relative flex size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink"
            >
              <ShoppingBag className="size-[18px]" aria-hidden />

              {cartCount > 0 ? (
                <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-pill bg-leaf-900 px-1 text-[10px] font-medium tabular-nums text-white">
                  {cartCount}
                </span>
              ) : null}
            </button>

            {/*
              Telefon ve WhatsApp dar ekranda gizlenir: altı ikon 360px'e
              sığmıyor ve başlık çubuğu taşıyordu. Bu iki aksiyon footer'da
              ve iletişim sayfasında zaten var — kaybolmuyor.
            */}
            {phone ? (
              <a
                href={`tel:${toTelNumber(phone)}`}
                aria-label="Telefonla ara"
                className="hidden size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink sm:flex"
              >
                <Phone className="size-[18px]" aria-hidden />
              </a>
            ) : null}

            {whatsapp ? (
              <a
                href={buildWhatsAppUrl(whatsapp, whatsappTemplate)}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="WhatsApp ile yaz"
                className="hidden size-10 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink sm:flex"
              >
                <MessageCircle className="size-[18px]" aria-hidden />
              </a>
            ) : null}
          </div>
        </div>

        {/* Mobil kategori şeridi */}
        <nav aria-label="Kategoriler" className="border-t border-line xl:hidden">
          <ul className="rail container-page gap-4 py-2.5">
            {categories?.map((category) => (
              <li key={category.id}>
                <Link
                  to={`/kategori/${category.slug}`}
                  className={cn(
                    'whitespace-nowrap text-sm transition-colors duration-fast',
                    pathname === `/kategori/${category.slug}`
                      ? 'text-ink'
                      : 'text-ink-muted',
                  )}
                >
                  {category.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <StorefrontFooter />
      <CartDrawer />
      <SearchOverlay />
    </div>
  );
}

function StorefrontFooter() {
  const { data: settings } = useStoreSettings();
  const { data: categories } = useCategories();

  /**
   * TESLİMAT BÖLGELERİ
   *
   * Yalnızca gerçekten hizmet verilen ve dizine girebilen sayfalar listelenir.
   * Footer'ı ilçe adlarıyla doldurmak (hizmet verilmeyenler dahil) hem
   * kullanıcıyı yanıltır hem de arama motoru için spam sinyalidir. En fazla
   * altı bağlantı gösterilir; gerisi şehir sayfasından ulaşılır.
   */
  const { data: localPages } = useLocalSeoPages();

  const deliveryLinks = (localPages ?? [])
    .filter((page) => page.kind === 'district' && page.indexable)
    .slice(0, 6);

  const cityPage = (localPages ?? []).find(
    (page) => page.kind === 'city' && page.indexable,
  );

  const storeName = settings?.['store.name'] ?? 'Hayal Çiçek & Hediyelik';
  const address = settings?.['store.address'];
  const city = settings?.['store.city'];
  const phone = settings?.['store.phone'];
  const email = settings?.['store.email'];
  const instagram = settings?.['social.instagram'];
  const workingHours = settings?.['store.workingHours'] ?? [];

  return (
    <footer className="mt-24 border-t border-line bg-surface">
      <div className="container-page py-14">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
          <div>
            <Wordmark storeName={storeName} size="md" className="items-start" />
            {address ? (
              <p className="mt-5 max-w-xs text-sm text-ink-muted">
                {address}
                {city ? `, ${city}` : ''}
              </p>
            ) : null}
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wide text-ink-subtle">
              Kategoriler
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {categories?.map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/kategori/${category.slug}`}
                    className="text-ink transition-colors duration-fast hover:text-leaf-600"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wide text-ink-subtle">
              İletişim
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {phone ? (
                <li>
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="text-ink transition-colors duration-fast hover:text-leaf-600"
                  >
                    {phone}
                  </a>
                </li>
              ) : null}
              {email ? (
                <li>
                  <a
                    href={`mailto:${email}`}
                    className="text-ink transition-colors duration-fast hover:text-leaf-600"
                  >
                    {email}
                  </a>
                </li>
              ) : null}
              {instagram ? (
                <li>
                  <a
                    href={instagram}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-2 text-ink transition-colors duration-fast hover:text-leaf-600"
                  >
                    <Instagram className="size-4" aria-hidden />
                    Instagram
                  </a>
                </li>
              ) : null}
            </ul>
          </div>

          <div>
            {deliveryLinks.length > 0 ? (
              <div className="mb-8">
                <h4 className="text-xs uppercase tracking-wide text-ink-subtle">
                  Teslimat bölgeleri
                </h4>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {deliveryLinks.map((page) => (
                    <li key={page.path}>
                      <Link
                        to={page.path}
                        className="text-ink transition-colors duration-fast hover:text-leaf-600"
                      >
                        {page.delivery?.zoneName ?? page.heading}
                      </Link>
                    </li>
                  ))}
                  {cityPage ? (
                    <li>
                      <Link
                        to={cityPage.path}
                        className="text-ink-subtle transition-colors duration-fast hover:text-leaf-600"
                      >
                        Tüm bölgeler
                      </Link>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            <h4 className="text-xs uppercase tracking-wide text-ink-subtle">
              Çalışma saatleri
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm text-ink-muted">
              {workingHours.map((entry) => (
                <li key={entry.day}>
                  <span className="block">{entry.day}</span>
                  <span className="text-ink">{entry.hours}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Kurumsal ve hukuki bağlantılar */}
        <nav
          aria-label="Kurumsal bağlantılar"
          className="mt-10 border-t border-line pt-6"
        >
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-subtle">
            {CORPORATE_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="transition-colors hover:text-ink">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 text-xs text-ink-subtle md:flex-row md:items-center md:justify-between">
          <span>
            © {new Date().getFullYear()} {storeName}
          </span>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/siparis-takip"
              className="transition-colors hover:text-ink"
            >
              Sipariş takibi
            </Link>
            <Link to="/favorilerim" className="transition-colors hover:text-ink">
              Favorilerim
            </Link>
            <span>Isparta</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
